// eslint-disable-next-line import/no-extraneous-dependencies
import {
    sendShopifyAnalytics,
    getClientBrowserParameters,
    AnalyticsEventName,
    AnalyticsPageType,
    ShopifySalesChannel,
} from "@shopify/hydrogen-react";
import { SHOPIFY_STOREFRONT_ID, sessionStorageKey, checkoutKey } from "utils/constants";
import { CATEGORIES } from "constants/categories";

/**
 * Domain for monorail produce_batch. Events go straight from the browser to the
 * Shopify-hosted subdomain (CORS allowed) so Shopify sees the visitor's real IP.
 * Do NOT proxy this through Netlify: a proxy makes every event look like it
 * comes from a Netlify/AWS datacenter (wrong geo, consent region, bot filtering).
 */
export const SHOPIFY_ANALYTICS_DOMAIN = "shop.sealskincovers.com";

const analyticsShopData = {
    shopId: `gid://shopify/Shop/${process.env.GATSBY_SHOP_ID}`,
    currency: "USD",
    acceptedLanguage: "en",
    // US-only store, no consent banner: mirror what Hydrogen sends when the
    // Customer Privacy API reports consent granted. hydrogen-react defaults
    // these to false, which marks every event as "analytics not allowed".
    hasUserConsent: true,
    analyticsAllowed: true,
    marketingAllowed: true,
    saleOfDataAllowed: true,
    // Storefront 1000017712 lives in the Hydrogen sales channel (not the Headless
    // channel), so events must be tagged with the Hydrogen app. Mixing the
    // Headless app id with a Hydrogen storefront id mis-files every session.
    shopifySalesChannel: ShopifySalesChannel.hydrogen,
    // Must match the Shopify-Storefront-Id header sent on cart/checkout requests
    // (services/storefront/common) so sessions and orders land in the same storefront.
    ...(SHOPIFY_STOREFRONT_ID ? { storefrontId: SHOPIFY_STOREFRONT_ID } : {}),
};

const toGid = (type, value) => {
    if (!value) return undefined;
    const str = String(value);
    if (str.startsWith("gid://")) return str;
    return `gid://shopify/${type}/${str}`;
};

/**
 * New-structure routes from create-pages.js:
 *   landing:    /{category}[/{make}[/{model}]]
 *               + /{year} when hasOptions  → AnalyticsPageType.page
 *               (no dedicated "landing" type in Shopify Analytics)
 *   collection: /{category}/{make}/{model}/{year}            (!hasOptions)
 *               /{category}/{make}/{model}/{year}/{option}   (hasOptions)
 *   product:    …/{slug}  (…/{option}/{slug} when hasOptions)
 *   by-style:   /{category}/by-style/...
 */
const getCategoryRoutePageType = (segments) => {
    const categorySlug = segments[0];
    const category = CATEGORIES?.[categorySlug];
    if (!category) return null;

    if (segments[1] === "by-style") {
        return AnalyticsPageType.collection;
    }

    // Matches create-pages.js segment counts for collection / product templates
    const productDepth = category.hasOptions ? 6 : 5;
    const collectionDepth = category.hasOptions ? 5 : 4;

    if (segments.length >= productDepth) return AnalyticsPageType.product;
    if (segments.length >= collectionDepth) return AnalyticsPageType.collection;
    // YMM funnel (category → make → model → year) = landing pages
    return AnalyticsPageType.page;
};

/**
 * Infer Shopify AnalyticsPageType from a pathname.
 */
export const getShopifyPageType = (pathname = "") => {
    const path = (pathname || "/").replace(/\/+$/, "") || "/";
    const segments = path.split("/").filter(Boolean);

    if (path === "/") return AnalyticsPageType.home;
    if (path === "/cart") return AnalyticsPageType.cart;
    if (path === "/search" || path.startsWith("/search/")) return AnalyticsPageType.search;
    if (path.includes("/products/")) return AnalyticsPageType.product;
    if (path.startsWith("/collections")) return AnalyticsPageType.collection;

    const categoryType = getCategoryRoutePageType(segments);
    if (categoryType) return categoryType;

    if (path.startsWith("/blogs/")) {
        return segments.length >= 3 ? AnalyticsPageType.article : AnalyticsPageType.blog;
    }
    if (path === "/account/login" || path.startsWith("/account/login")) {
        return AnalyticsPageType.customersLogin;
    }
    if (path === "/account/register" || path.startsWith("/account/register")) {
        return AnalyticsPageType.customersRegister;
    }
    if (path.startsWith("/account/orders")) return AnalyticsPageType.customersOrder;
    if (path.startsWith("/account/addresses") || path === "/addresses") {
        return AnalyticsPageType.customersAddresses;
    }
    if (path.startsWith("/account")) return AnalyticsPageType.customersAccount;
    if (path === "/404" || path.startsWith("/404")) return AnalyticsPageType.notFound;

    return AnalyticsPageType.page;
};

export const getCollectionHandleFromPath = (pathname = "") => {
    const path = (pathname || "/").replace(/\/+$/, "") || "/";
    const segments = path.split("/").filter(Boolean);

    const match = path.match(/^\/collections\/([^/]+)/);
    if (match && !path.includes("/products/")) {
        return decodeURIComponent(match[1]);
    }

    // True collection routes (incl. by-style) — stable handle for collection_page_rendered
    if (CATEGORIES?.[segments[0]] && getShopifyPageType(path) === AnalyticsPageType.collection) {
        return segments.join("/");
    }

    return undefined;
};

export const getSearchStringFromLocation = (search = "") => {
    if (!search) return undefined;
    const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
    return params.get("q") || params.get("query") || params.get("search") || undefined;
};

export const getShopifyCustomerId = (explicitId) => {
    if (explicitId) return toGid("Customer", explicitId);
    if (typeof window === "undefined") return undefined;
    try {
        const raw = window.sessionStorage.getItem(sessionStorageKey);
        if (!raw) return undefined;
        const user = JSON.parse(raw);
        return user?.id ? toGid("Customer", user.id) : undefined;
    } catch {
        return undefined;
    }
};

export const getShopifyCartId = () => {
    if (typeof window === "undefined") return undefined;
    const id = window.localStorage.getItem(checkoutKey);
    return id || undefined;
};

/**
 * Map app / cart product shapes into ShopifyAnalyticsProduct[].
 */
export const mapShopifyAnalyticsProducts = (products = []) => [].concat(products).map((input) => {
    const product = input?.node ?? input;
    const variant = product?.currentVariant?.node
        ?? product?.variant
        ?? product?.variants?.[0]?.node
        ?? product?.variants?.edges?.[0]?.node;

    const productId = product?.shopify_id
        ?? variant?.product?.id
        ?? product?.id
        ?? product?.product_id;
    const variantId = variant?.id ?? product?.item_id ?? product?.variantGid;
    const priceRaw = variant?.price?.amount ?? variant?.price ?? product?.price;
    const price = priceRaw != null ? String(priceRaw) : "0";

    const productGid = toGid("Product", productId);
    const variantGid = variantId ? toGid("ProductVariant", variantId) : undefined;

    return {
        productGid,
        ...(variantGid ? { variantGid } : {}),
        name: product?.title || product?.item_name || product?.name || "",
        variantName: variant?.title || product?.item_variant || product?.variantName || undefined,
        brand: product?.vendor || product?.item_brand || "Seal Skin Covers",
        category: product?.productType
            || product?.item_metrics?.item_category
            || product?.item_category
            || undefined,
        price,
        quantity: Number(product?.quantity ?? 1),
        ...(variant?.sku || product?.sku ? { sku: variant?.sku || product?.sku } : {}),
    };
}).filter((p) => p.productGid);

const buildBasePayload = (extra = {}) => {
    const customerId = getShopifyCustomerId(extra.customerId);
    const { customerId: _ignored, ...rest } = extra;

    return {
        ...getClientBrowserParameters(),
        ...analyticsShopData,
        ...(customerId ? { customerId } : {}),
        ...rest,
    };
};

const send = (eventName, payload) => sendShopifyAnalytics(
    { eventName, payload },
    SHOPIFY_ANALYTICS_DOMAIN,
);

/**
 * @param {object} [pageData]
 * @param {string} [pageData.pageType]
 * @param {string} [pageData.collectionHandle]
 * @param {string} [pageData.searchString]
 * @param {string} [pageData.customerId]
 * @param {Array} [pageData.products]
 * @param {number} [pageData.totalValue]
 * @param {string} [pageData.canonicalUrl]
 * @param {string} [pageData.resourceId]
 */
export const trackPageView = (pageData = {}) => {
    const payload = buildBasePayload(pageData);
    return send(AnalyticsEventName.PAGE_VIEW, payload);
};

/**
 * Product detail view with product payload (in addition to route PAGE_VIEW).
 */
export const trackShopifyProductView = (products, pageData = {}) => {
    const mapped = mapShopifyAnalyticsProducts(products);
    const totalValue = mapped.reduce(
        (sum, p) => sum + (parseFloat(p.price) || 0) * (p.quantity || 1),
        0,
    );

    return send(
        AnalyticsEventName.PRODUCT_VIEW,
        buildBasePayload({
            pageType: AnalyticsPageType.product,
            products: mapped,
            totalValue,
            ...pageData,
        }),
    );
};

export const trackShopifyCollectionView = (collectionHandle, pageData = {}) => send(
    AnalyticsEventName.COLLECTION_VIEW,
    buildBasePayload({
        pageType: AnalyticsPageType.collection,
        collectionHandle,
        ...pageData,
    }),
);

export const trackShopifySearchView = (searchString, pageData = {}) => send(
    AnalyticsEventName.SEARCH_VIEW,
    buildBasePayload({
        pageType: AnalyticsPageType.search,
        searchString,
        ...pageData,
    }),
);

/**
 * Shopify ADD_TO_CART (Monorail). Requires a cart id (gid://shopify/Cart/...).
 */
export const trackShopifyAddToCart = (products, { cartId, customerId } = {}) => {
    const resolvedCartId = cartId || getShopifyCartId();
    if (!resolvedCartId) return Promise.resolve();

    const mapped = mapShopifyAnalyticsProducts(products);
    if (!mapped.length) return Promise.resolve();

    const totalValue = mapped.reduce(
        (sum, p) => sum + (parseFloat(p.price) || 0) * (p.quantity || 1),
        0,
    );

    return send(
        AnalyticsEventName.ADD_TO_CART,
        buildBasePayload({
            cartId: resolvedCartId,
            products: mapped,
            totalValue,
            ...(customerId ? { customerId } : {}),
        }),
    );
};

export { AnalyticsPageType, AnalyticsEventName, ShopifySalesChannel };
